import { authedFetch } from "./restClient.js";
import { grpcStatusName } from "./grpcStatus.js";

// One read carries at most this many references; the server enforces the
// same number (MAX_REFERENCES in shared-analytics).
export const MAX_CONTENT_REFERENCES = 256;

// Transport JSON carries protobuf fields. No field contains an encoded JSON
// document, and integer oneofs use decimal strings on this boundary.
export async function readConversationContent(request, { signal } = {}) {
    if (!request?.references?.length || request.references.length > MAX_CONTENT_REFERENCES) {
        throw new Error(`Select 1 to ${MAX_CONTENT_REFERENCES} conversation items.`);
    }
    const response = await authedFetch("/v1/conversations:readContent", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request), signal,
    });
    if (!response.ok) {
        const error = new Error(`Conversation content is unavailable (${response.status}).`);
        error.status = response.status;
        // The gateway's envelope names the refusal. Its numeric code is the
        // stable discriminator a reader can branch on; the message is prose.
        const text = await Promise.resolve().then(() => response.text()).catch(() => "");
        let envelope = null;
        try { envelope = JSON.parse(text); } catch { /* non-JSON body: proxy page or empty */ }
        error.grpcCode = typeof envelope?.code === "number" ? grpcStatusName(envelope.code) : "";
        if (typeof envelope?.message === "string") error.serverMessage = envelope.message;
        throw error;
    }
    const result = await response.json();
    if (result.contents?.length !== request.references.length) {
        throw new Error("Conversation content changed while loading. Try again.");
    }
    result.contents.forEach((content, index) => {
        const reference = request.references[index];
        const [kind] = Object.keys(reference);
        const returned = content.reference?.[kind];
        if (!returned || !Object.entries(reference[kind]).every(([field, value]) =>
            (returned[field] ?? (typeof value === "number" ? 0 : "")) === value)) {
            throw new Error("Conversation content changed while loading. Try again.");
        }
    });
    return result.contents;
}

export function conversationObjectToPlain(object) {
    return Object.fromEntries((object?.fields || []).map(({ key, value }) => [key, conversationValueToPlain(value)]));
}

export function conversationValueToPlain(value) {
    if (!value) return null;
    if (value.nullValue === true) return null;
    for (const key of ["stringValue", "boolValue", "floatValue"]) {
        if (Object.hasOwn(value, key)) return value[key];
    }
    for (const key of ["integerValue", "unsignedIntegerValue", "bigIntegerValue"]) {
        if (Object.hasOwn(value, key)) {
            const number = Number(value[key]);
            // Existing block consumers need ordinary JS values. Retain exact
            // digits as text when Number cannot represent an integer; the
            // original typed value remains on the block for precise display.
            return Number.isSafeInteger(number) ? number : String(value[key]);
        }
    }
    if (value.listValue) return (value.listValue.values || []).map(conversationValueToPlain);
    if (value.objectValue) return conversationObjectToPlain(value.objectValue);
    if (value.truncated) return { type: "analysis-truncated", ...value.truncated };
    throw new Error("Conversation value has no content.");
}

export function formatConversationValue(value, indent = 0) {
    const pad = "  ".repeat(indent);
    const childPad = `${pad}  `;
    for (const key of ["integerValue", "unsignedIntegerValue", "bigIntegerValue"]) {
        if (Object.hasOwn(value || {}, key)) return String(value[key]);
    }
    if (value?.floatValue !== undefined) {
        const text = String(value.floatValue);
        return Number.isInteger(value.floatValue) && !/[eE]/.test(text) ? `${text}.0` : text;
    }
    if (value?.listValue) {
        const parts = (value.listValue.values || []).map((item) => `${childPad}${formatConversationValue(item, indent + 1)}`);
        return parts.length ? `[\n${parts.join(",\n")}\n${pad}]` : "[]";
    }
    if (value?.objectValue) {
        const parts = (value.objectValue.fields || []).map(({ key, value: item }) =>
            `${childPad}${JSON.stringify(key)}: ${formatConversationValue(item, indent + 1)}`);
        return parts.length ? `{\n${parts.join(",\n")}\n${pad}}` : "{}";
    }
    return JSON.stringify(conversationValueToPlain(value));
}

export function conversationPartToBlock(part) {
    if (!part) return null;
    const extra = part.providerFields ? { providerFields: conversationObjectToPlain(part.providerFields) } : {};
    if (part.text !== undefined) return { type: "text", text: part.text, ...extra };
    if (part.reasoning !== undefined) return { type: "thinking", thinking: part.reasoning, ...extra };
    if (part.toolUse) return {
        type: "tool_use", id: part.toolUse.sourceCallId ?? null, name: part.toolUse.name || "",
        input: conversationValueToPlain(part.toolUse.input), typedInput: part.toolUse.input, ...extra,
    };
    if (part.toolResult) return {
        type: "tool_result", tool_use_id: part.toolResult.sourceCallId ?? null,
        tool_name: part.toolResult.toolName ?? null, is_error: Boolean(part.toolResult.isError), isError: Boolean(part.toolResult.isError),
        linkedToolName: part.toolResult.toolName || undefined,
        content: conversationValueToPlain(part.toolResult.content), typedContent: part.toolResult.content, ...extra,
    };
    if (part.image) return { type: "image", ...conversationObjectToPlain(part.image.metadata), ...extra };
    if (part.audio) return { type: "audio", ...conversationObjectToPlain(part.audio.metadata), ...extra };
    if (part.unknown) return { ...conversationObjectToPlain(part.unknown), ...extra };
    if (part.truncated) return { type: "analysis-truncated", ...part.truncated, ...extra };
    throw new Error("Conversation part has no content.");
}
